
import { Fragment, type FC, useState, useEffect } from 'react';
import {Dialog, DialogPanel, DialogTitle, Transition, TransitionChild} from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { OrderItem } from '../../types';

interface EditItemModalProps {
    isOpen: boolean;
    item: OrderItem | null;
    onClose: () => void;
    onSave: (previousQuantity: number, currentQuantity: number, reason: string) => Promise<void>;
    onDeleteTriggered?: (reason: string) => void; // 👈 NEW
    isSubmitting: boolean;
}

const labelClass = 'block text-sm font-medium text-gray-800';
const fieldClass =
    'block w-full rounded-lg border-0 ring-1 ring-inset ring-stone-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-stone-400 focus:ring-2 focus:ring-gray-900 focus:outline-none';

const EditItemModal: FC<EditItemModalProps> = ({ isOpen, item, onClose, onSave, onDeleteTriggered, isSubmitting }) => {
    const [currentQuantity, setCurrentQuantity] = useState<number>(0);
    const [reason, setReason] = useState<string>('');
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (isOpen && item) {
            setCurrentQuantity(item.quantity);
        } else {
            // Reset form when modal closes
            setCurrentQuantity(0);
            setReason('');
            setError('');
        }
    }, [isOpen, item]);

    const handleSave = async () => {
        setError('');

        if (!reason.trim()) {
            setError('Please provide a reason for the change');
            return;
        }

        if (currentQuantity < 0) {
            setError('Quantity cannot be negative');
            return;
        }

        // 👈 NEW: If quantity is 0, trigger delete instead
        if (currentQuantity === 0) {
            if (onDeleteTriggered) {
                onDeleteTriggered(reason.trim());
            }
            return;
        }

        if (currentQuantity === item?.quantity) {
            setError('Quantity must be different from the original');
            return;
        }

        try {
            await onSave(item!.quantity, currentQuantity, reason.trim());
            setCurrentQuantity(0);
            setReason('');
            setError('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save changes');
        }
    };

    return (
        <Transition show={isOpen && !!item} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-[1px]" />
                </TransitionChild>

                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
                        <TransitionChild
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
                        >
                            <DialogPanel className="relative w-full transform overflow-hidden rounded-2xl bg-white ring-1 ring-stone-200 shadow-xl transition-all sm:max-w-md">
                                <div className="absolute right-0 top-0 pr-4 pt-4">
                                    <button
                                        type="button"
                                        className="rounded-md text-stone-400 hover:text-stone-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
                                        onClick={onClose}
                                        aria-label="Close"
                                    >
                                        <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                                    </button>
                                </div>

                                <div className="px-5 py-6 sm:px-6">
                                    <DialogTitle as="h3" className="font-display text-xl tracking-tight text-gray-900">
                                        Edit item quantity
                                    </DialogTitle>
                                    <p className="mt-1 text-sm text-gray-600">Update the quantity and provide a reason for the change.</p>

                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            void handleSave();
                                        }}
                                        className="mt-6 space-y-5"
                                    >
                                        <div>
                                            <label className={labelClass}>Item</label>
                                            <div className="mt-1 w-full rounded-lg border-0 ring-1 ring-inset ring-stone-300 bg-stone-50 px-3 py-2 text-sm text-gray-900 font-medium">
                                                {item?.product_name}
                                            </div>
                                        </div>

                                        <div>
                                            <label className={labelClass}>Current quantity</label>
                                            <div className="mt-1 w-full rounded-lg border-0 ring-1 ring-inset ring-stone-300 bg-stone-50 px-3 py-2 text-sm text-gray-900 font-medium">
                                                {item?.quantity}
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="new_quantity" className={labelClass}>
                                                New quantity
                                            </label>
                                            <input
                                                type="number"
                                                id="new_quantity"
                                                min="0"
                                                max="9999"
                                                value={currentQuantity}
                                                onChange={(e) => setCurrentQuantity(Math.max(0, parseInt(e.target.value) || 0))}
                                                className={fieldClass}
                                                disabled={isSubmitting}
                                                autoFocus
                                            />
                                            {currentQuantity === 0 && item?.quantity !== 0 && (
                                                <p className="mt-2 text-xs text-amber-600">Setting quantity to 0 will delete this item</p>
                                            )}
                                        </div>

                                        <div>
                                            <label htmlFor="reason" className={labelClass}>
                                                Reason for change <span className="text-red-600">*</span>
                                            </label>
                                            <textarea
                                                id="reason"
                                                value={reason}
                                                onChange={(e) => setReason(e.target.value)}
                                                placeholder="e.g., Damaged item, customer request, inventory correction…"
                                                className={`${fieldClass} resize-none`}
                                                rows={3}
                                                disabled={isSubmitting}
                                            />
                                        </div>

                                        {currentQuantity !== item?.quantity && currentQuantity >= 0 && (
                                            <div className={`rounded-lg p-3 ring-1 ring-inset ${
                                                currentQuantity === 0
                                                    ? 'bg-red-50 ring-red-200'
                                                    : 'bg-[#e6186c]/10 ring-[#e6186c]'
                                            }`}>
                                                <p className={`text-xs font-semibold ${currentQuantity === 0 ? 'text-red-900' : 'text-[#e6186c]'}`}>
                                                    {currentQuantity === 0 ? 'Delete warning' : 'Change summary'}
                                                </p>
                                                <p className={`mt-1 text-sm ${currentQuantity === 0 ? 'text-red-800' : 'text-[#e6186c]'}`}>
                                                    {currentQuantity === 0
                                                        ? `This will delete the item from the order`
                                                        : `${item?.quantity} → ${currentQuantity} (${currentQuantity - (item?.quantity || 0) > 0 ? '+' : ''}${currentQuantity - (item?.quantity || 0)})`
                                                    }
                                                </p>
                                            </div>
                                        )}

                                        {error && (
                                            <div className="rounded-lg bg-red-50 ring-1 ring-inset ring-red-200 p-3">
                                                <p className="text-sm text-red-900">{error}</p>
                                            </div>
                                        )}

                                        <div className="pt-2 flex items-center justify-end gap-3">
                                            <button
                                                type="button"
                                                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-stone-300 hover:bg-stone-50 disabled:opacity-50"
                                                onClick={onClose}
                                                disabled={isSubmitting}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                disabled={isSubmitting}
                                                className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm  focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-70 disabled:cursor-not-allowed ${
                                                    currentQuantity === 0
                                                        ? 'bg-red-600 hover:bg-red-700 focus-visible:outline-red-600'
                                                        : 'bg-gray-900 hover:bg-gray-800 focus-visible:outline-gray-900'
                                                }`}
                                            >
                                                {isSubmitting ? 'Saving…' : currentQuantity === 0 ? 'Delete item' : 'Save changes'}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default EditItemModal;
